import { buildCollection, buildProperty } from "@firecms/core";

// Proficiency levels matching backend
const proficiencyLevels = ["Pre-A1", "A1", "A2", "B1", "B2", "C1", "C2"] as const;

export type LanguageProfile = {
    language_code: string;
    language_name?: string;
    proficiency_level?: string;
    // Per-level progress tracking
    level_progress?: Record<string, {
        lessons_count: number;
        concepts_covered: string[];
        started_at?: Date;
        last_lesson_at?: Date;
    }>;
    // Flattened stats
    stats_streak: number;
    stats_longest_streak: number;
    stats_total_sessions: number;
    stats_sessions_today: number;
    stats_last_session_date?: Date;
    stats_last_streak_update?: Date;
    // Timestamps
    created_at: Date;
    last_active_at: Date;
};

export const languagesSubcollection = buildCollection<LanguageProfile>({
    id: "languages",
    name: "Languages",
    singularName: "Language Profile",
    path: "languages",
    icon: "Translate",
    description: "Learning languages with per-language progress and stats",
    defaultSize: "s",
    permissions: {
        edit: true,
        create: true,
        delete: true,
    },
    properties: {
        language_code: buildProperty({
            name: "Language Code",
            dataType: "string",
            description: "ISO 639-1 code (e.g., en, nl, fa)",
            validation: { required: true },
        }),
        language_name: buildProperty({
            name: "Language Name",
            dataType: "string",
            description: "Display name (e.g., English, Dutch)",
        }),
        proficiency_level: buildProperty({
            name: "Proficiency Level",
            dataType: "string",
            enumValues: Object.fromEntries(proficiencyLevels.map((l) => [l, l])),
            description: "CEFR proficiency level for this language",
        }),
        level_progress: buildProperty({
            name: "Level Progress",
            dataType: "map",
            description: "Curriculum progress per proficiency level",
            readOnly: true,
            keyValue: true,
            expanded: false,
            properties: {
                lessons_count: buildProperty({
                    name: "Lessons Completed",
                    dataType: "number",
                }),
                concepts_covered: buildProperty({
                    name: "Concepts Covered",
                    dataType: "array",
                    of: { dataType: "string" },
                }),
                started_at: buildProperty({
                    name: "Started At",
                    dataType: "date",
                    mode: "date_time",
                }),
                last_lesson_at: buildProperty({
                    name: "Last Lesson",
                    dataType: "date",
                    mode: "date_time",
                }),
            },
        }),
        stats_streak: buildProperty({
            name: "Current Streak",
            dataType: "number",
            readOnly: true,
        }),
        stats_longest_streak: buildProperty({
            name: "Longest Streak",
            dataType: "number",
            readOnly: true,
        }),
        stats_total_sessions: buildProperty({
            name: "Total Sessions",
            dataType: "number",
            readOnly: true,
        }),
        stats_sessions_today: buildProperty({
            name: "Sessions Today",
            dataType: "number",
            readOnly: true,
        }),
        stats_last_session_date: buildProperty({
            name: "Last Session",
            dataType: "date",
            mode: "date",
            readOnly: true,
        }),
        stats_last_streak_update: buildProperty({
            name: "Streak Updated",
            dataType: "date",
            readOnly: true,
        }),
        created_at: buildProperty({
            name: "Created",
            dataType: "date",
            mode: "date_time",
            readOnly: true,
        }),
        last_active_at: buildProperty({
            name: "Last Active",
            dataType: "date",
            mode: "date_time",
            readOnly: true,
        }),
    },
    propertiesOrder: [
        "language_code",
        "language_name",
        "proficiency_level",
        "stats_streak",
        "stats_total_sessions",
        "last_active_at",
    ],
});
